'use client';

/**
 * MonoLabel — the tiny mono-caps caption used as a form-field label
 * and as a section header inside ProjectForm / TechniqueForm.
 *
 * Two sizes:
 *   sm (default) — 9px / 0.16em / fw 700 / display:block / marginBottom 5
 *                   The field-label recipe. Used above text inputs and
 *                   pickers ("PROJECT NAME", "STATUS *", "NOTES").
 *   md            — 10px / 0.14em / fw 700 / display:inline / no margin
 *                   The section-header recipe. Used as the heading for
 *                   grouped form sections ("YARN", "NEEDLES",
 *                   "TECHNIQUES", "ACCESSORIES", "LINKS").
 *
 * Override the bottom margin with `mb` for the few field labels that
 * need a different gap (the photo + status fields use 8 instead of 5).
 *
 * Always renders as a `<label>` so callers can pair it with an input
 * via `htmlFor` when needed. The section-header use case is also a
 * `<label>` today — preserving that to avoid an unrelated semantic
 * change. If we want to move section headers to `<p>` or `<h3>`
 * later, add an `as` prop.
 */

import type { CSSProperties, ReactNode } from 'react';

export type MonoLabelSize = 'sm' | 'md';

export type MonoLabelProps = {
  children: ReactNode;
  size?: MonoLabelSize;
  /** Override the bottom margin. Default: 5 for sm, 0 for md. */
  mb?: number | string;
  htmlFor?: string;
  style?: CSSProperties;
  className?: string;
};

const SIZE_STYLES: Record<MonoLabelSize, CSSProperties> = {
  sm: { display: 'block', fontSize: 9, letterSpacing: '0.16em' },
  md: { fontSize: 10, letterSpacing: '0.14em' },
};

export function MonoLabel({
  children,
  size = 'sm',
  mb,
  htmlFor,
  style,
  className,
}: MonoLabelProps) {
  const resolvedMb = mb !== undefined ? mb : size === 'sm' ? 5 : undefined;
  return (
    <label
      htmlFor={htmlFor}
      className={className}
      style={{
        fontFamily: 'var(--font-mono)',
        fontWeight: 700,
        textTransform: 'uppercase',
        color: 'var(--ink-2)',
        ...SIZE_STYLES[size],
        ...(resolvedMb !== undefined ? { marginBottom: resolvedMb } : null),
        ...style,
      }}
    >
      {children}
    </label>
  );
}
